/**
 * Klien staf dan PIN.
 *
 * Pengurus mencipta kasir dan menetapkan PIN di back office. Kasir log masuk pada PERANTI
 * (ditandatangani Ed25519), jadi PIN tidak boleh digunakan dari telefon atau PC lain.
 */

import { signPayload } from './sync.js';

export interface StaffRow {
  user_id: string;
  name: string;
  role: string;
  status: string;
  has_pin: boolean;
  locked_until: string | null;
}

async function parseError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  return new Error(body?.error?.message ?? `permintaan gagal (HTTP ${response.status})`);
}

export async function listStaff(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<StaffRow[]> {
  const response = await fetchImpl(`${apiBase}/v1/staff`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
  return ((await response.json()) as { data: StaffRow[] }).data;
}

export async function createStaff(
  apiBase: string,
  token: string,
  input: { name: string; pin: string },
  fetchImpl: typeof fetch = fetch,
): Promise<{ user_id: string }> {
  const response = await fetchImpl(`${apiBase}/v1/staff`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as { user_id: string };
}

export async function resetStaffPin(
  apiBase: string,
  token: string,
  userId: string,
  pin: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/staff/${userId}/pin`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  if (!response.ok) throw await parseError(response);
}

export async function disableStaff(
  apiBase: string,
  token: string,
  userId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const response = await fetchImpl(`${apiBase}/v1/staff/${userId}/disable`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
}

/** Senarai kasir untuk skrin pilih nama — ditandatangani peranti, tiada e-mel. */
export async function fetchStaffForDevice(
  apiBase: string,
  deviceId: string,
  privateKey: string | CryptoKey,
  fetchImpl: typeof fetch = fetch,
): Promise<Array<{ id: string; name: string }>> {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signPayload(privateKey, timestamp, '');
  const response = await fetchImpl(`${apiBase}/v1/sync/staff`, {
    headers: {
      'x-device-id': deviceId,
      'x-device-timestamp': String(timestamp),
      'x-device-signature': signature,
    },
  });
  if (!response.ok) throw await parseError(response);
  return ((await response.json()) as { data: Array<{ id: string; name: string }> }).data;
}

/**
 * Log masuk kasir dengan PIN.
 *
 * Badan ditandatangani MENTAH — tandatangan meliputi `${timestamp}.${rawBody}` — jadi ia mesti
 * dihasilkan sebelum dihantar, bukan selepas penguraian.
 */
export async function pinLoginForDevice(
  apiBase: string,
  deviceId: string,
  privateKey: string | CryptoKey,
  input: { userId: string; pin: string },
  fetchImpl: typeof fetch = fetch,
): Promise<{ token: string; name: string }> {
  const raw = JSON.stringify({ user_id: input.userId, pin: input.pin });
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signPayload(privateKey, timestamp, raw);

  const response = await fetchImpl(`${apiBase}/v1/auth/pin`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-device-id': deviceId,
      'x-device-timestamp': String(timestamp),
      'x-device-signature': signature,
    },
    body: raw,
  });

  if (!response.ok) throw await parseError(response);
  const body = (await response.json()) as { access_token: string; user: { name: string } };
  return { token: body.access_token, name: body.user.name };
}

/**
 * Adakah sesi kasir ini masih sah?
 *
 * Dipanggil semasa POS dibuka. Sesi PIN luput selepas 12 jam, dan tanpa semakan ini POS akan terus
 * memaparkan "Kasir: A" walaupun pelayan sudah tidak mengaitkan jualan dengan sesiapa.
 *
 * Ralat rangkaian TIDAK dianggap tidak sah: kedai mesti boleh berjual semasa offline.
 */
export async function sessionStillValid(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  try {
    const response = await fetchImpl(`${apiBase}/v1/auth/me`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (response.status === 401) return false;
    return true;
  } catch {
    return true;
  }
}
